/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable no-trailing-spaces */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Table, message, Spin, Row, Col, Button } from "antd";
import { ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import { HubCPOverviewService } from "../../../services/api/hubCPOverviewService";
import "./index.scss";

const HubManagerShipmentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Extract parameters from navigation state
  const {
    operationCodes,
    hubId,
    dateFilter,
    title,
    selectedPeriod,
    isPieChart,
    status,
    fromDate,
    toDate,
    bookingDateFrom,
    bookingDateTo,
  } = state || {};

  // Table columns
  let columns = [];

  if (isPieChart) {
    columns = [
      {
        title: "Booking Date",
        dataIndex: "bookingDate",
        key: "bookingDate",
        width: 150,
      },
      {
        title: "Tracking ID",
        dataIndex: "trackingId",
        key: "trackingId",
        width: 180,
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              if (text) {
                const url = `/shipment-tracker?awb=${text}`;
                window.open(url, "_blank");
              }
            }}
          >
            {text || "-"}
          </span>
        ),
      },
      {
        title: "Movement Type",
        dataIndex: "movementType",
        key: "movementType",
        width: 150,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 150,
      },
      {
        title: "Document Type",
        dataIndex: "documentType",
        key: "documentType",
        width: 150,
      },
      {
        title: "Service Type",
        dataIndex: "serviceType",
        key: "serviceType",
        width: 150,
      },
      {
        title: "Origin Hub",
        dataIndex: "originHub",
        key: "originHub",
        width: 200,
      },
      {
        title: "Current Location",
        dataIndex: "currentLocation",
        key: "currentLocation",
        width: 200,
      },
      {
        title: "Next Location",
        dataIndex: "nextLocation",
        key: "nextLocation",
        width: 200,
      },
      {
        title: "Destination Hub",
        dataIndex: "destinationHub",
        key: "destinationHub",
        width: 200,
      },
      {
        title: "Latest Status Time",
        dataIndex: "latestStatusTime",
        key: "latestStatusTime",
        width: 180,
      },
      {
        title: "Ageing (Days)",
        dataIndex: "ageingFromStatusDateInDays",
        key: "ageingFromStatusDateInDays",
        width: 120,
      },
    ];
  } else {
    columns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
      },
      {
        title: "AWB Number",
        dataIndex: "awbNumber",
        key: "awbNumber",
        width: 180,
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              if (text) {
                const url = `/shipment-tracker?awb=${text}`;
                window.open(url, "_blank");
              }
            }}
          >
            {text || "-"}
          </span>
        ),
      },
      {
        title: "Operation Hub",
        dataIndex: "operationHub",
        key: "operationHub",
        width: 200,
      },
      {
        title: "Origin Hub",
        dataIndex: "originHub",
        key: "originHub",
        width: 200,
        render: (text) => text || "-",
      },
      {
        title: "Destination Hub",
        dataIndex: "destinationHub",
        key: "destinationHub",
        width: 200,
        render: (text) => text || "-",
      },
    ];
  }

  // Fetch data
  const fetchData = async (pageNum = 1, pageSize = 10) => {
    if ((!isPieChart && (!operationCodes || !hubId || !dateFilter)) || (isPieChart && !hubId)) {
      message.warning("Missing required parameters");
      return;
    }

    try {

      setLoading(true);
      let response;

      if (isPieChart) {
        response = await HubCPOverviewService.getPieChartHubManagerOverviewClickable({
          currentHubId: hubId,
          status: status,
          page: pageNum - 1,
          size: pageSize,
          fromDate,
          toDate,
          bookingDateFrom,
          bookingDateTo,
        });

        if (response && response.data) {
          // Standardize response for consistent handling below if needed, 
          // but the state setter logic below handles response.data directly.
          // API returns { data: [], count: ... } or just data array?
          // Service returns response.response.data which should be the object structure.
          // However, let's look at service: getPieChart... returns response.response.data directly.
          // API response: { code: 200, data: [...], count: ... }
          // So response here is that object.
        }
      } else {
        response = await HubCPOverviewService.getClickableHubManagerDashboard({
          dateFilter: dateFilter,
          operationCodes: operationCodes,
          hubId: hubId,
          page: pageNum - 1,
          size: pageSize,
        });
      }

      if (response?.data) {
        setData(response.data);
        setTotalCount(response.totalCount || response.count || 0); // Handle both totalCount and count
        setPagination((prev) => ({
          ...prev,
          current: pageNum,
          pageSize: pageSize,
          total: response.totalCount || response.count || 0,
        }));
      }
    } catch (error) {
      message.error(error?.message || "Failed to fetch shipment details");
      console.error("Error fetching shipment details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state) {
      fetchData(1, 10);
    } else {
      message.warning("No parameters provided. Redirecting...");
      setTimeout(() => {
        navigate("/hub-overview");
      }, 2000);
    }
    // eslint-disable-next-line
  }, []);

  // Handle table pagination
  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    setPagination((prev) => ({
      ...prev,
      current: current,
      pageSize: pageSize,
    }));
    fetchData(current, pageSize);
  };

  const getPeriodLabel = (period) => {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "tillDate":
        return "Till Date";
      default:
        return period;
    }
  };

  return (
    <div className="hub-manager-shipment-details">
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/hub-overview")}
              style={{ marginBottom: 16 }}
            >
              Back to Overview
            </Button>
            <h2 style={{ margin: 0, marginTop: 8 }}>
              {title || "Shipment Details"}
            </h2>
          </div>
          <div style={{ paddingTop: "50px" }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={async () => {
                try {

                  let url;
                  if (isPieChart) {
                    url = await HubCPOverviewService.getPieChartHubManagerOverviewClickableDownload({
                      currentHubId: hubId,
                      status: status,
                      fromDate,
                      toDate,
                      bookingDateFrom,
                      bookingDateTo,
                    });
                  } else {
                    url = await HubCPOverviewService.getClickableHubManagerDashboardDownload({
                      dateFilter: dateFilter,
                      operationCodes: operationCodes,
                      hubId: hubId,
                      status: state?.status || null,
                    });
                  }
                  if (url) {
                    window.open(url, "_blank");
                  }
                } catch (error) {
                  message.error(error?.message || "Failed to download report");
                  console.error("Download error:", error);
                }
              }}
            >
              Download
            </Button>
          </div>
        </div>

        <div className="summary-info" style={{ marginBottom: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
          {selectedPeriod && (
            <div style={{ marginBottom: 8 }}>
              <strong>Period:</strong> {getPeriodLabel(selectedPeriod)}
            </div>
          )}
          <div>
            <strong>Total Count:</strong> {totalCount.toLocaleString()}
          </div>
          {(fromDate || toDate) && (
            <div style={{ marginTop: 8 }}>
              <strong>Latest Status Date:</strong> {fromDate || "N/A"} to {toDate || "N/A"}
            </div>
          )}
          {(bookingDateFrom || bookingDateTo) && (
            <div style={{ marginTop: 8 }}>
              <strong>Booking Date:</strong> {bookingDateFrom || "N/A"} to {bookingDateTo || "N/A"}
            </div>
          )}
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            rowKey={(record, index) => `${record.awbNumber}-${index}`}
            scroll={{ x: 900 }}
            size="small"
            locale={{ emptyText: "No shipment details available" }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default HubManagerShipmentDetails;

